<template>
  <PriorHeader />
  <v-container>
    <v-row>
      <v-col cols="12">
        <SearchPatientByExternalIdView ref="searchPatientRef" @prepopulate-order-info="handlePrepopulateOrderInfo" />
        <h1 class="text-primary weight-600 pb-4">Create New Submission</h1>
        <v-row>
          <v-col cols="12" class="submission-detail-section">
            <v-container class="patient-information-form-wrapper">
              <div v-if="preloader">
                <SubmissionPageSkeleton />
              </div>
              <div v-else>
                <!-- Error display -->
                <div v-if="errors.length > 0" class="mt-4">
                  <v-alert type="error">
                    <div v-for="error in errors" :key="error">{{ error }}</div>
                  </v-alert>
                </div>
                <v-row>
                  <v-col cols="12" class="submission-detail-section">
                    <v-expansion-panels v-if="!preloader" v-model="expandedSections" multiple>
                      <!-- Patient Information Section -->
                      <v-expansion-panel value="patient-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-account" class="mr-2"></v-icon>
                          Patient Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <PatientInformationFormPreview
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Additional Patient Charts (multi-patient chart support) -->
                      <v-alert
                        v-if="
                          orderInfo.additionalPatientExternalIds && orderInfo.additionalPatientExternalIds.length > 0
                        "
                        type="info"
                        variant="tonal"
                        class="mb-4">
                        <div class="text-subtitle-2 mb-1">Additional Patient Charts</div>
                        <div class="d-flex flex-wrap ga-2">
                          <v-chip
                            v-for="patientChart in orderInfo.additionalPatientExternalIds"
                            :key="patientChart.externalId"
                            size="small"
                            variant="outlined">
                            {{ patientChart.firstName }} {{ patientChart.lastName }} — MRN:
                            {{ patientChart.externalId }}
                          </v-chip>
                        </div>
                      </v-alert>

                      <!-- Provider Information Section -->
                      <v-expansion-panel value="provider-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-doctor" class="mr-2"></v-icon>
                          Provider Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <ProviderInformationFormPreview
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Medical Information Section -->
                      <v-expansion-panel value="medical-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-medication" class="mr-2"></v-icon>
                          Prescription Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <MedicalInformationForm
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            @update-order-info-field="updateOrderInfoField"
                            @add-extra-medication="addExtraMedication"
                            @remove-extra-medication="removeExtraMedication"
                            @remove-primary-medication="removePrimaryMedication"
                            @update-extra-medication="updateExtraMedicationField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Patient Diagnosis Information Section -->
                      <v-expansion-panel value="diagnosis-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-account-heart" class="mr-2"></v-icon>
                          Patient Diagnosis Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <v-row class="patient-information-container bg-snow rounded-16">
                            <v-col cols="12">
                              <v-row class="patient-information">
                                <v-col cols="12" class="header-with-select-box">
                                  <DiagnosisSelectionPreviewComponent
                                    :medication="orderInfo.medication"
                                    :order-key="orderInfo.orderKey"
                                    :primary-diagnosis-name="orderInfo.primaryDiagnosisName"
                                    :primary-diagnosis-icd10-code="orderInfo.primaryDiagnosisIcd10Code"
                                    :secondary-diagnosis="orderInfo.secondaryDiagnosis"
                                    :is-editable="true"
                                    :form-button-clicked="formButtonClicked"
                                    @update-primary-diagnosis="updatePrimaryDiagnosis"
                                    @update-other-diagnosis="updateOtherDiagnosis"
                                    @add-other-diagnosis="addOtherDiagnosis" />
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Supporting Documents Section -->
                      <v-expansion-panel value="supporting-docs">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-file-document" class="mr-2"></v-icon>
                          Supporting Documents
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <SupportingDocumentsPreviewComponent
                            :order-key="orderInfo.orderKey"
                            :medical-necessity="orderInfo.medicalNecessity"
                            :patient-chart="orderInfo.patientChart"
                            :medical-necessity-template="orderInfo.medicalNecessityTemplate"
                            :supplemental-docs="supplementalDocs"
                            :form-button-clicked="formButtonClicked"
                            :is-editable="true"
                            @add-uploaded-file="updateSupportingDocs"
                            @delete-uploaded-file="handleDeleteFile"
                            @add-free-form-supplemental-docs="addFreeFormSupplementalDocs" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Validation status -->
                    <v-card class="mb-4 mt-4">
                      <v-card-title>Validation Status</v-card-title>
                      <v-card-text>
                        <v-chip :color="validPatientInfo() ? 'success' : 'error'" class="mr-2">
                          Patient Info: {{ validPatientInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validProviderInfo() ? 'success' : 'error'" class="mr-2">
                          Provider Info: {{ validProviderInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validMedicationInfo() ? 'success' : 'error'" class="mr-2">
                          Medication Info: {{ validMedicationInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validDiagnosisInfo() ? 'success' : 'error'" class="mr-2">
                          Diagnosis Info: {{ validDiagnosisInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                      </v-card-text>
                    </v-card>

                    <!-- Action buttons for new submission creation -->
                    <v-row class="mt-4">
                      <v-col cols="auto">
                        <v-btn
                          variant="outlined"
                          :disabled="draftSavingInProgress"
                          :loading="draftSavingInProgress"
                          @click="handleSaveDraft">
                          Save Draft
                        </v-btn>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn :disabled="draftSavingInProgress" @click="handleCreateSubmission">
                          Create Submission
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
            </v-container>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="route.params?.id">
      <v-col cols="12">
        <br />
        <AuditLogList :order-info-id="route.params?.id" />
      </v-col>
    </v-row>
    <v-dialog v-model="showCreateSuccessDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6" color="green">Submission Created</v-card-title>
        <v-card-text>
          Your submission has been created successfully. Fetching form and questions from external portal may take up to
          15 minutes. You may return to all submissions or create another submission.
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn variant="outlined" @click="onSuccessReturnToAll">Return to All Submissions</v-btn>
          <v-btn variant="flat" color="primary" @click="onSuccessCreateAnother">Create Another Submission</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  <PriorFooter />
</template>

<script setup>
import { ref, onBeforeMount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useOrderInfoUpdates } from "@/composables/useOrderInfoUpdates";
import { tryParseApiErrors, clearOrderInfoStorage } from "@/utils";
import PriorHeader from "@/components/PriorHeader.vue";
import PriorFooter from "@/components/PriorFooter.vue";
import PatientInformationFormPreview from "@/pages/PatientInformationFormPreview.vue";
import ProviderInformationFormPreview from "@/pages/ProviderInformationFormPreview.vue";
import MedicalInformationForm from "@/pages/MedicalInformationForm.vue";
import DiagnosisSelectionPreviewComponent from "@/components/DiagnosisSelectionPreviewComponent.vue";
import SupportingDocumentsPreviewComponent from "@/components/SupportingDocumentsPreviewComponent.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import SearchPatientByExternalIdView from "@/components/SearchPatientByExternalIdView.vue";
import { orderInfoService } from "@/services/orderInfoService";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const route = useRoute();
const {
  supplementalDocs,
  formButtonClicked,
  updateOrderInfoField,
  addExtraMedication,
  removeExtraMedication,
  removePrimaryMedication,
  updateExtraMedicationField,
  addOtherDiagnosis,
  updatePrimaryDiagnosis,
  updateOtherDiagnosis,
  updateSupportingDocs,
  addFreeFormSupplementalDocs,
  validPatientInfo,
  validProviderInfo,
  validMedicationInfo,
  validDiagnosisInfo,
  validSupportingDocs,
  savePartialOrderInfo,
  mergeSupplementalDocsWithPatientChart,
} = useOrderInfoUpdates(orderInfo);

function handleDeleteFile(downloadLinkInfo) {
  // Mark as delete and use existing updateSupportingDocs handler
  downloadLinkInfo.isDelete = true;
  updateSupportingDocs(downloadLinkInfo);
}

const router = useRouter();
const expandedSections = ref([]);
const draftSavingInProgress = ref(false);
const errors = ref([]);
const preloader = ref(false);
const showCreateSuccessDialog = ref(false);
const searchPatientRef = ref(null);

function handlePrepopulateOrderInfo(prepopulatedData) {
  // List of all fields that can be prepopulated from intake data
  // Clear these fields first to prevent old values from persisting when selection changes
  const prepopulatableFields = [
    // Patient fields
    "patientFirstName",
    "patientLastName",
    "patientDob",
    "patientExternalId",
    // Insurance fields
    "patientMemberId",
    "insuranceProvider",
    "insurancePlanType",
    "benefitType",
    "bin",
    "processorControlNumber",
    "grpNumber",
    // Medication fields
    "medication",
    "dailyDosage",
    "nationalDrugCode",
    "frequency",
    "expectedLengthOfTherapy",
    "quantityPer30Days",
    "daw",
    // Provider fields
    "providerFirstName",
    "providerLastName",
    "providerTitle",
    "providerNpiNumber",
    "providerContactFax",
    "providerContactPhone",
    "providerAddress1",
    "providerCity",
    "providerState",
    "providerZip",
    "providerOfficeName",
    // Patient demographics from order form
    "patientGender",
    "patientAddress1",
    "patientCity",
    "patientState",
    "patientZip",
    "patientWeight",
    "patientDrugAllergies",
    // Diagnosis fields
    "primaryDiagnosisIcd10Code",
    "primaryDiagnosisName",
    // Treatment
    "treatmentStartDate",
    // patientChart - replace, don't merge when new selection is made
    "patientChart",
    // Additional patient chart MRNs (multi-patient chart support)
    "additionalPatientExternalIds",
  ];

  // Clear all prepopulatable fields first to ensure old values don't persist
  prepopulatableFields.forEach((field) => {
    if (field === "patientChart" || field === "additionalPatientExternalIds") {
      updateOrderInfoField(field, []);
    } else {
      updateOrderInfoField(field, null);
    }
  });

  // Now apply the new prepopulated data
  Object.keys(prepopulatedData).forEach((field) => {
    if (prepopulatedData[field] !== null && prepopulatedData[field] !== undefined) {
      // Special handling for patientChart - replace with new data (already cleared above)
      if (field === "patientChart" && Array.isArray(prepopulatedData[field])) {
        updateOrderInfoField(field, prepopulatedData[field]);
      } else {
        updateOrderInfoField(field, prepopulatedData[field]);
      }
    }
  });
}

onBeforeMount(() => {
  clearOrderInfoStorage();
  if (route.params.id) {
    loadOrder(route.params.id);
  } else {
    initializeOrderInfo();
  }
  supplementalDocs.value.push({ label: null, links: [] });

  formButtonClicked.value = true;
  expandedSections.value = ["patient-info", "provider-info", "medical-info", "diagnosis-info", "supporting-docs"];
});

function initializeOrderInfo() {
  orderInfo.value = {
    ...orderInfo.value,
    formStep: "Review",
    submissionMethod: "CoverMyMeds",
    extraMedications: [],
    secondaryDiagnosis: [{ icd10Code: null, name: null }],
    patientChart: [],
  };
}

async function loadOrder(orderKey) {
  preloader.value = true;
  try {
    errors.value = [];
    const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
    orderInfo.value = fetchedOrder.orderInfo;
    orderInfo.value.formStep = "Review";
    orderInfo.value.submissionMethod = "CoverMyMeds";
  } catch (err) {
    errors.value = ["Failed to load the order. Please try again later or start a new submission."];
    initializeOrderInfo();
  }
  preloader.value = false;
}

async function handleSaveDraft() {
  draftSavingInProgress.value = true;
  try {
    errors.value = [];
    await savePartialOrderInfo();
  } catch (error) {
    errors.value = ["Failed to save draft. Please try again."];
  }
  draftSavingInProgress.value = false;
}

async function handleCreateSubmission() {
  formButtonClicked.value = true;
  if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
    mergeSupplementalDocsWithPatientChart();
  }
  errors.value = [];
  if (!validPatientInfo()) {
    expandedSections.value.push("patient-info");
    errors.value.push("Please fill in all required patient information fields.");
  }
  if (!validProviderInfo()) {
    expandedSections.value.push("provider-info");
    errors.value.push("Please fill in all required provider information fields.");
  }
  if (!validMedicationInfo()) {
    expandedSections.value.push("medical-info");
    errors.value.push("Please fill in all required medication information fields.");
  }
  if (!validDiagnosisInfo()) {
    expandedSections.value.push("diagnosis-info");
    errors.value.push("Please fill in all required diagnosis information fields.");
  }
  if (!validSupportingDocs()) {
    expandedSections.value.push("supporting-docs");
    errors.value.push("Please add at least one supporting document.");
  }
  await new Promise((resolve) => setTimeout(resolve, 300));
  const errorClassNames = ["v-input--error", "form-validation-alert"];
  let missingInput = [];
  errorClassNames.forEach((className) => {
    const foundElements = document.getElementsByClassName(className);
    missingInput = [...missingInput, ...foundElements];
  });
  if (errors.value.length === 0 && missingInput.length === 0) {
    draftSavingInProgress.value = true;
    try {
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }
      orderInfo.value.formStep = "Create Submission";
      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value);
      errors.value = [];
      expandedSections.value = [];
      showCreateSuccessDialog.value = true;
    } catch (err) {
      errors.value = tryParseApiErrors(err);
    }
    draftSavingInProgress.value = false;
  } else if (missingInput.length > 0) {
    setTimeout(() => {
      const parentDiv = missingInput[0].parentNode;
      window.scrollTo({
        top: parentDiv.offsetTop,
        behavior: "smooth",
      });
    }, 0);
  }
}

function onSuccessReturnToAll() {
  showCreateSuccessDialog.value = false;
  clearOrderInfoStorage();
  router.push({ name: "all-submissions" });
  window.scrollTo(0, 0);
}

function onSuccessCreateAnother() {
  showCreateSuccessDialog.value = false;
  clearOrderInfoStorage();
  initializeOrderInfo();
  supplementalDocs.value = [{ label: null, links: [] }];
  expandedSections.value = ["patient-info", "provider-info", "medical-info", "diagnosis-info", "supporting-docs"];
  errors.value = [];
  formButtonClicked.value = true;
  searchPatientRef.value?.resetSearch?.();
  router.replace({ name: "create-submission" });
  window.scrollTo(0, 0);
}
</script>

<style lang="scss" scoped></style>
